import React, { useEffect, useMemo, useState } from 'react';
import { UserDetails } from './UserDetails';
import {
  ArrowLeft,
  UserRound,
  Building2,
  ChevronRight,
  Landmark,
  HandCoins,
} from 'lucide-react';
import { useUnifiedCustomers, formatWorkspaceEntryDate, useAdminEmiPayments, getFinanceFacilityMeta, type UnifiedCustomer, type UnifiedLoanFacility } from '../../lib/workspaceStore';
import { useTranslation } from '../../lib/i18n';
import { PageSearchBar } from '../../components/common/PageSearchBar';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useDateRange, inRange, formatRangeLabel } from '../../lib/dateFilter';
import { ExportMenu, type ExportRow } from '../../lib/exportReports';
import { mediaSrc } from '../../lib/mediaUrl';

interface UserListProps {
  onClose: () => void;
  setActiveItem?: (item: string) => void;
  /** When set, open this borrower's profile immediately (e.g. from notifications). */
  focusUserId?: string | null;
  /** Called once the focus id has been applied so the parent can clear it. */
  onConsumeFocus?: () => void;
}

type LoanTypeFilter = 'all' | 'finance' | 'handloan';
type FileStatusFilter = 'all' | 'active' | 'closed';

function initials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function formatInr(n: number) {
  return `₹${Math.round(Math.max(0, n)).toLocaleString('en-IN')}`;
}

function customerLoanKinds(user: UnifiedCustomer): { finance: boolean; handloan: boolean } {
  let finance = false;
  let handloan = false;
  for (const f of user.facilities) {
    if (f.kind === 'finance') finance = true;
    if (f.kind === 'handloan') handloan = true;
  }
  return { finance, handloan };
}

function isFacilityClosed(f: UnifiedLoanFacility, payoffClosedIds: Set<string>): boolean {
  if (payoffClosedIds.has(f.recordId)) return true;
  if (f.kind === 'finance') {
    const meta = getFinanceFacilityMeta(f.recordId);
    if (meta?.closedOnRecord) return true;
  }
  return false;
}

/** Active = at least one open file; Closed = every file closed. */
function customerFileStatus(
  user: UnifiedCustomer,
  payoffClosedIds: Set<string>
): { active: boolean; closed: boolean; allClosed: boolean; openCount: number; closedCount: number } {
  let openCount = 0;
  let closedCount = 0;
  for (const f of user.facilities) {
    if (isFacilityClosed(f, payoffClosedIds)) closedCount += 1;
    else openCount += 1;
  }
  return {
    active: openCount > 0,
    closed: closedCount > 0,
    allClosed: user.facilities.length > 0 && openCount === 0,
    openCount,
    closedCount,
  };
}

export function UserList({ onClose, setActiveItem, focusUserId, onConsumeFocus }: UserListProps) {
  const users = useUnifiedCustomers();
  const adminPayments = useAdminEmiPayments();
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(focusUserId ?? null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loanType, setLoanType] = useState<LoanTypeFilter>('all');
  const [fileStatus, setFileStatus] = useState<FileStatusFilter>('all');
  const dr = useDateRange('all');

  useEffect(() => {
    if (focusUserId) {
      setSelectedUserId(focusUserId);
      onConsumeFocus?.();
    }
  }, [focusUserId, onConsumeFocus]);

  const payoffClosedIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of adminPayments) {
      if (p.facilityPayoff) s.add(p.recordId);
    }
    return s;
  }, [adminPayments]);

  const typeCounts = useMemo(() => {
    let finance = 0;
    let handloan = 0;
    for (const u of users) {
      const k = customerLoanKinds(u);
      if (k.finance) finance += 1;
      if (k.handloan) handloan += 1;
    }
    return { all: users.length, finance, handloan };
  }, [users]);

  const statusCounts = useMemo(() => {
    let active = 0;
    let closed = 0;
    for (const u of users) {
      const s = customerFileStatus(u, payoffClosedIds);
      if (s.active) active += 1;
      if (s.allClosed) closed += 1;
    }
    return { all: users.length, active, closed };
  }, [users, payoffClosedIds]);

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (dr.range.start || dr.range.end) {
      if (!inRange(user.createdAt, dr.range)) return false;
    }
    if (loanType !== 'all') {
      const k = customerLoanKinds(user);
      if (loanType === 'finance' && !k.finance) return false;
      if (loanType === 'handloan' && !k.handloan) return false;
    }
    if (fileStatus !== 'all') {
      const s = customerFileStatus(user, payoffClosedIds);
      if (fileStatus === 'active' && !s.active) return false;
      if (fileStatus === 'closed' && !s.allClosed) return false;
    }
    return true;
  });

  const listTotals = useMemo(() => {
    let principal = 0;
    let collected = 0;
    let outstanding = 0;
    for (const u of filteredUsers) {
      principal += u.totalLoan || 0;
      collected += u.paidLoan || 0;
      outstanding += u.pendingLoan || 0;
    }
    return { principal, collected, outstanding };
  }, [filteredUsers]);

  const exportRows: ExportRow[] = filteredUsers.map((u) => {
    const k = customerLoanKinds(u);
    const s = customerFileStatus(u, payoffClosedIds);
    const types = [k.finance ? 'Finance' : null, k.handloan ? 'Hand loan' : null].filter(Boolean).join(' + ');
    return {
      Borrower: u.name,
      Phone: u.phone || '',
      Email: u.email || '',
      'Loan type': types || '—',
      Status: s.allClosed ? 'Closed' : s.closed ? `Active (${s.openCount}) + Closed (${s.closedCount})` : 'Active',
      'Entry date': formatWorkspaceEntryDate(u.createdAt),
      Facilities: u.facilities.length,
      Principal: Math.round(u.totalLoan),
      Collected: Math.round(u.paidLoan),
      Outstanding: Math.round(u.pendingLoan),
    };
  });

  const selected: UnifiedCustomer | undefined = selectedUserId
    ? users.find((u) => u.id === selectedUserId)
    : undefined;

  if (selectedUserId) {
    if (!selected) {
      return (
        <div className="space-y-4 p-4">
          <button
            type="button"
            onClick={() => setSelectedUserId(null)}
            className="text-sm font-medium text-zinc-800 hover:underline"
          >
            ← Back to list
          </button>
          <p className="text-sm text-gray-600">
            This customer is no longer in the workspace (records may have been removed).
          </p>
        </div>
      );
    }
    return (
      <UserDetails
        user={selected}
        onBack={() => setSelectedUserId(null)}
        setActiveItem={setActiveItem}
        onLeaveUsersView={onClose}
      />
    );
  }

  return (
    <div className="app-page min-w-0 max-w-full space-y-4 overflow-x-hidden sm:space-y-6">
      <header className="app-page-header !mb-0">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('customers.backOverview')}
          </button>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800 dark:text-emerald-400">
            {t('nav.customers')}
          </p>
          <h1 className="app-page-title mt-0.5">{t('customers.pageTitle')}</h1>
          <p className="app-page-sub">
            Borrowers from finance and hand-loan facilities — search, filter, and open profiles.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          <span className="rounded-full border border-zinc-200/90 bg-white px-3 py-1.5 font-semibold text-zinc-700 shadow-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
            {filteredUsers.length} of {users.length} borrower{users.length === 1 ? '' : 's'}
          </span>
          <label className="inline-flex items-center gap-1.5">
            <span className="sr-only">{t('customers.filterLoanType')}</span>
            <select
              value={loanType}
              onChange={(e) => setLoanType(e.target.value as LoanTypeFilter)}
              aria-label={t('customers.filterLoanType')}
              className="h-9 max-w-[9.5rem] rounded-lg border border-zinc-200 bg-white px-2 text-xs font-semibold text-zinc-800 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/25"
            >
              <option value="all">
                {t('customers.filterAll')} ({typeCounts.all})
              </option>
              <option value="finance">
                {t('customers.filterFinance')} ({typeCounts.finance})
              </option>
              <option value="handloan">
                {t('customers.filterHand')} ({typeCounts.handloan})
              </option>
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5">
            <span className="sr-only">{t('customers.filterFileStatus')}</span>
            <select
              value={fileStatus}
              onChange={(e) => setFileStatus(e.target.value as FileStatusFilter)}
              aria-label={t('customers.filterFileStatus')}
              className="h-9 max-w-[9rem] rounded-lg border border-zinc-200 bg-white px-2 text-xs font-semibold text-zinc-800 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/25"
            >
              <option value="all">
                {t('customers.filterStatusAll')} ({statusCounts.all})
              </option>
              <option value="active">
                {t('customers.filterActive')} ({statusCounts.active})
              </option>
              <option value="closed">
                {t('customers.filterClosed')} ({statusCounts.closed})
              </option>
            </select>
          </label>
          <ExportMenu title={`${t('customers.exportTitle')} (${formatRangeLabel(dr.range)})`} excelFilename="customers.xls" rows={exportRows} />
        </div>
      </header>

      <div className="app-kpi-strip">
        <div className="app-kpi-tile border border-zinc-200/90 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <p className="app-kpi-tile-label">{t('common.borrower')}s</p>
          <p className="app-kpi-tile-value">{filteredUsers.length}</p>
        </div>
        <div className="app-kpi-tile border border-zinc-200/90 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <p className="app-kpi-tile-label">{t('common.principal')}</p>
          <p className="app-kpi-tile-value">{formatInr(listTotals.principal)}</p>
        </div>
        <div className="app-kpi-tile border border-zinc-200/90 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <p className="app-kpi-tile-label">{t('common.collected')}</p>
          <p className="app-kpi-tile-value">{formatInr(listTotals.collected)}</p>
        </div>
        <div className="app-kpi-tile border border-zinc-200/90 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <p className="app-kpi-tile-label">{t('common.outstanding')}</p>
          <p className="app-kpi-tile-value">{formatInr(listTotals.outstanding)}</p>
        </div>
      </div>

      <div className="app-panel !p-3 sm:!p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <PageSearchBar
          id="customer-list-search"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search name, email, or phone…"
          className="max-w-xl"
        />
        <DateRangeFilter
          start={dr.range.start}
          end={dr.range.end}
          preset={dr.preset}
          onStart={dr.setStart}
          onEnd={dr.setEnd}
          onPreset={dr.setPreset}
          onClear={dr.clear}
          compact
        />
      </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300/80 bg-gradient-to-br from-zinc-50 to-white px-6 py-14 text-center dark:border-zinc-600 dark:from-zinc-900 dark:to-zinc-800/50">
          <UserRound className="mx-auto h-10 w-10 text-zinc-400" />
          <p className="mt-3 font-semibold text-zinc-900">{t('common.noMatch')}</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-zinc-600">
            {fileStatus === 'active'
              ? 'No borrowers with active files match these filters.'
              : fileStatus === 'closed'
                ? 'No borrowers with closed files match these filters.'
                : loanType === 'finance'
                  ? 'No borrowers with finance loans match these filters.'
                  : loanType === 'handloan'
                    ? 'No borrowers with hand loans match these filters.'
                    : t('customers.addHint')}
          </p>
          {setActiveItem ? (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  setActiveItem('finance');
                }}
                className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800"
              >
                {t('customers.financeDesk')}
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  setActiveItem('loans');
                }}
                className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-900 hover:bg-zinc-50"
              >
                {t('customers.handLoans')}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="app-data-table-wrap hidden lg:block">
            <table className="app-data-table w-full text-left text-sm">
              <thead>
                <tr>
                  <th>{t('common.borrower')}</th>
                  <th>{t('customers.filterLoanType')}</th>
                  <th>{t('common.entryDate')}</th>
                  <th>{t('common.facilities')}</th>
                  <th className="text-right">{t('common.principal')}</th>
                  <th className="text-right">{t('common.collected')}</th>
                  <th className="text-right">{t('common.outstanding')}</th>
                  <th className="text-right" />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const kinds = customerLoanKinds(user);
                  const status = customerFileStatus(user, payoffClosedIds);
                  return (
                    <tr
                      key={user.id}
                      className="cursor-pointer hover:bg-emerald-50/70 dark:hover:bg-emerald-950/25"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          {user.avatar ? (
                            <img src={mediaSrc(user.avatar)} alt="" className="app-avatar-ring h-11 w-11 shrink-0 rounded-full object-cover" />
                          ) : (
                            <div className="app-avatar-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-600/25 to-teal-600/20 text-xs font-bold text-emerald-950 dark:text-emerald-100">
                              {initials(user.name)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-zinc-900 dark:text-zinc-100">{user.name}</p>
                            <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">{user.email}</p>
                            {user.phone ? <p className="truncate text-[11px] text-zinc-400 dark:text-zinc-500">{user.phone}</p> : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {kinds.finance ? (
                            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200/70">
                              <Landmark className="h-3 w-3" />
                              Finance
                            </span>
                          ) : null}
                          {kinds.handloan ? (
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200/70">
                              <HandCoins className="h-3 w-3" />
                              Hand
                            </span>
                          ) : null}
                          {status.allClosed ? (
                            <span className="inline-flex rounded-md bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600 ring-1 ring-zinc-200/80">
                              {t('customers.badgeClosed')}
                            </span>
                          ) : status.closed ? (
                            <>
                              <span className="inline-flex rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800 ring-1 ring-sky-200/70">
                                {t('customers.badgeActive')} {status.openCount}
                              </span>
                              <span className="inline-flex rounded-md bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600 ring-1 ring-zinc-200/80">
                                {t('customers.badgeClosed')} {status.closedCount}
                              </span>
                            </>
                          ) : (
                            <span className="inline-flex rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800 ring-1 ring-sky-200/70">
                              {t('customers.badgeActive')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-xs text-zinc-600 dark:text-zinc-400">
                        {formatWorkspaceEntryDate(user.createdAt)}
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1 rounded-lg border border-zinc-200/80 bg-zinc-50 px-2.5 py-1 text-xs font-semibold text-zinc-800 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
                          <Building2 className="h-3 w-3 text-zinc-500" />
                          {user.facilities.length}
                        </span>
                      </td>
                      <td className="text-right font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{formatInr(user.totalLoan)}</td>
                      <td className="text-right font-semibold tabular-nums text-emerald-800 dark:text-emerald-400">{formatInr(user.paidLoan)}</td>
                      <td className="text-right font-semibold tabular-nums text-amber-900 dark:text-amber-400">
                        {user.pendingLoan > 0 ? formatInr(user.pendingLoan) : '—'}
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedUserId(user.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200/80 bg-emerald-50/50 px-3 py-1.5 text-xs font-semibold text-emerald-900 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100/60 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/60"
                        >
                          {t('common.profile')}
                          <ChevronRight className="h-3.5 w-3.5 opacity-60" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 lg:hidden">
            {filteredUsers.map((user) => {
              const kinds = customerLoanKinds(user);
              const status = customerFileStatus(user, payoffClosedIds);
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUserId(user.id)}
                  className="group flex w-full min-w-0 max-w-full flex-col rounded-xl border border-zinc-200/90 bg-white p-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    {user.avatar ? (
                      <img src={mediaSrc(user.avatar)} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover ring-1 ring-zinc-200" />
                    ) : (
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-600/20 to-teal-600/15 text-sm font-bold text-emerald-950 ring-1 ring-emerald-200/60">
                        {initials(user.name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="flex min-w-0 items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <p className="truncate font-semibold text-zinc-900">{user.name}</p>
                          <p className="truncate text-xs text-zinc-500">{user.email}</p>
                        </div>
                        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-zinc-300 transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-600" />
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {kinds.finance ? (
                          <span className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-200/60">
                            <Landmark className="h-2.5 w-2.5" />
                            Finance
                          </span>
                        ) : null}
                        {kinds.handloan ? (
                          <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 ring-1 ring-amber-200/60">
                            <HandCoins className="h-2.5 w-2.5" />
                            Hand
                          </span>
                        ) : null}
                        {status.allClosed ? (
                          <span className="inline-flex rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600 ring-1 ring-zinc-200/70">
                            {t('customers.badgeClosed')}
                          </span>
                        ) : status.closed ? (
                          <>
                            <span className="inline-flex rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 ring-1 ring-sky-200/60">
                              {t('customers.badgeActive')} {status.openCount}
                            </span>
                            <span className="inline-flex rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600 ring-1 ring-zinc-200/70">
                              {t('customers.badgeClosed')} {status.closedCount}
                            </span>
                          </>
                        ) : (
                          <span className="inline-flex rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 ring-1 ring-sky-200/60">
                            {t('customers.badgeActive')}
                          </span>
                        )}
                      </div>
                      <div className="mt-2.5 grid grid-cols-2 gap-1.5 text-[11px]">
                        <div className="min-w-0 overflow-hidden rounded-lg bg-zinc-50 px-2 py-1.5">
                          <p className="truncate text-zinc-500">Principal</p>
                          <p className="truncate font-semibold tabular-nums text-zinc-800">{formatInr(user.totalLoan)}</p>
                        </div>
                        <div className="min-w-0 overflow-hidden rounded-lg bg-emerald-50/80 px-2 py-1.5">
                          <p className="truncate text-emerald-800/80">Collected</p>
                          <p className="truncate font-semibold tabular-nums text-emerald-950">{formatInr(user.paidLoan)}</p>
                        </div>
                        {user.pendingLoan > 0 ? (
                          <div className="col-span-2 flex min-w-0 items-center justify-between gap-2 overflow-hidden rounded-lg bg-amber-50 px-2 py-1.5 text-amber-950">
                            <span className="shrink-0 font-medium">Outstanding</span>
                            <span className="min-w-0 truncate font-bold tabular-nums">{formatInr(user.pendingLoan)}</span>
                          </div>
                        ) : null}
                        <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-zinc-500">
                          <Building2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {user.facilities.length} facility{user.facilities.length === 1 ? '' : 'ies'}
                          </span>
                          <span className="text-zinc-300">·</span>
                          <span className="truncate">Entry {formatWorkspaceEntryDate(user.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
